import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Stack, styled, Theme, useTheme } from '@mui/material';
import { MoreInfoSliderProps } from '../types';
import { SchoolInfoHighlightRow } from '../HighlightRows';
import { SchoolTagsSection } from '../SchoolTags/SchoolTagsSection';
import { ConnectSchoolTagDisplayAtLocations } from 'features/Connect/utils/connectEnums';
import { SaveSchoolButton } from '../Buttons/SaveSchoolButton';
import { ExpressInterestButton } from '../Buttons/ExpressInterestButton';
import { PrincipalComponent } from '../PrincipalComponent/PrincipalComponent';
import { QuickApplyButton } from '../Buttons/QuickApplyButton';
import { ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds } from 'data-testids/ConnectDataTestIds';

export const HeaderSection: React.FC<MoreInfoSliderProps> = ({ school, isCandidateDashboard }) => {
  const theme = useTheme();

  const hasPrincipal = !!school?.principal;
  const hasTags = !!school?.tags;

  return (
    <MoreInfoHeaderContainer>
      {school && (
        <Stack spacing={1} gap={0.5}>
          <TitleContainer>
            <SchoolName>{school.name}</SchoolName>
            <SaveSchoolButton school={school} size="small" sx={saveSchoolButtonStyles(theme)} />
          </TitleContainer>
          {!isCandidateDashboard && (
            <ButtonGroupStack>
              <ExpressInterestButton
                school={school}
                sx={expressInterestAndQuickApplyButtonStyles(theme)}
              />
              {school?.is_internal_school && (
                <QuickApplyButton
                  sx={expressInterestAndQuickApplyButtonStyles(theme)}
                  isDistrictQuickApply={true}
                  school={school}
                  dataTestId={
                    ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds.DISTRICT_QUICK_APPLY_BUTTON
                  }
                />
              )}
            </ButtonGroupStack>
          )}
          {hasPrincipal && (
            <PrincipalComponent principal={school.principal} isMoreInfoSlider={true} />
          )}
          <InfoRow>
            {school?.school_metadata?.school_type}
            <span> &#183; </span>
            {school?.district?.name}
          </InfoRow>
          <SchoolInfoHighlightRow school={school} isInterestModal={false} isMoreInfoSlider={true} />
          {hasTags && (
            <SchoolTagsSection
              school={school}
              display_at={ConnectSchoolTagDisplayAtLocations.MORE_INFO_SLIDER}
            />
          )}
        </Stack>
      )}
    </MoreInfoHeaderContainer>
  );
};

const MoreInfoHeaderContainer = styled(Box)(() => ({
  width: '100%',
}));

const SchoolName = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMediumBold,
  fontSize: '18px',
  color: theme.palette.gray.dark,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    whiteSpace: 'normal',
  },
}));

const InfoRow = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMediumBold,
  fontSize: theme.typography.body2.fontSize,
  color: '#666',
  marginTop: theme.spacing(0.5),
}));

const TitleContainer = styled(Stack)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginRight: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    marginRight: theme.spacing(0),
  },
}));

const ButtonGroupStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(1),
  margin: theme.spacing(0.75, 0),
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1),
    width: '100%',
    '& > *': {
      flex: 1,
      minWidth: 0,
    },
  },
}));

const saveSchoolButtonStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius * 0.5,
  minWidth: '32px',
  width: '32px',
  height: '32px',
  alignItems: 'center',
});

const expressInterestAndQuickApplyButtonStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius * 0.5,
  height: '32px',
  alignItems: 'center',
});
